import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { Button, Icon, Input, Loader } from '@weezevent/nacre';
import { fileExtensionsToAccept } from '../../../nacre/uploadButton/stateless/mimeTypes';
import '../uploadFileZone.scss';
import i18n from '../../../i18n/i18n';
import * as mime from 'react-native-mime-types';

export async function dataUrlToFileUsingFetch(url, fileName, mimeType) {
    const response = await fetch(url);
    const buffer = await response.arrayBuffer();

    return new File([buffer], fileName, { type: mimeType });
}

export function UploadPreviewPanelButton({ imageSrc = null, fileTypes = [], onDelete = () => {}, onChangeEditImage = () => {} }) {
    const { t } = useTranslation();
    const acceptedTypes = React.useMemo(() => {
        return fileExtensionsToAccept(fileTypes);
    }, [fileTypes]);

    async function _onChangeEditImage(imgSrc, _acceptedTypes) {
        const fileName = imgSrc.substring(imgSrc.lastIndexOf('/') + 1);
        const extension = fileName.substring(fileName.lastIndexOf('.'));
        const typeMime = mime.lookup(extension);
        if (Object.keys(_acceptedTypes).includes(typeMime)) {
            const _file = await dataUrlToFileUsingFetch(imgSrc, fileName, typeMime);
            onChangeEditImage(_file);
        }
    }

    if (!imageSrc) {
        return null;
    }

    return (
        <div className={'upload-preview-button-panel__container'}>
            <div style={{ display: 'flex' }}>
                {Boolean(onChangeEditImage) && (
                    <Button
                        className={'upload-preview-button-panel__edit'}
                        label={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Icon name={'edit'} />
                                <div>{t('ticket.common.action.edit')}</div>
                            </div>
                        }
                        onClick={() => _onChangeEditImage(imageSrc, acceptedTypes)}
                    />
                )}
                <Button className={'upload-preview-button-panel__delete'} onClick={onDelete} label={<Icon name={'trash'} margin={false} />} />
            </div>
        </div>
    );
}

UploadPreviewPanelButton.propTypes = {
    imageSrc: PropTypes.string,
    fileTypes: PropTypes.array,
    onDelete: PropTypes.func,
    onChangeEditImage: PropTypes.func
};

export function PlaceholderErrorLoading() {
    const { t } = useTranslation();

    return (
        <div className="placeholder-error-loading">
            <Icon size={'huge'} name={'message-warning'} />
            <div>{t('ticket.event.dropzone_image_error_loading')}</div>
        </div>
    );
}

export function UploadPreviewFile({
    errorLoading = false,
    isUploadedS3 = true,
    onLoad = () => {},
    onError = () => {},
    onDelete = null,
    fileSrc = null,
    fileTypes = [],
    displayDate = true
}) {
    const { t } = useTranslation();
    const locale = i18n.language;
    const [fileInfos, setFileInfos] = React.useState(null);

    React.useEffect(() => {
        if (isUploadedS3 && fileSrc) {
            fetch(fileSrc)
                .then(({ headers }) => {
                    setFileInfos({
                        size: headers.get('content-length'),
                        lastModified: headers.get('last-modified') || headers.get('date')
                    });
                    onLoad();
                })
                .catch(() => {
                    setFileInfos(null);
                    onError();
                });
        }
    }, [fileSrc, onLoad, onError, isUploadedS3]);

    const size = React.useCallback(() => {
        if (!fileInfos) {
            return '';
        }
        const sizeMo = Math.trunc((fileInfos.size / 1000000) * 10) / 10;
        if (sizeMo >= 1) {
            return `(${sizeMo} Mo)`;
        }

        return `(${Math.trunc((fileInfos.size / 1000) * 10) / 10} Ko)`;
    }, [fileInfos]);

    const fileName = React.useMemo(() => {
        return `${fileSrc.substring(fileSrc.lastIndexOf('/') + 1)} ${size()}`;
    }, [fileSrc, size]);

    const lastModifiedDate = React.useMemo(() => {
        if (!fileInfos) {
            return '';
        }

        const options = {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: false
        };

        return new Intl.DateTimeFormat(locale, options).format(new Date(fileInfos.lastModified));
    }, [fileInfos]);

    const onClick = React.useCallback(() => {
        if (!errorLoading && fileInfos) {
            window.open(fileSrc);
        }
    }, [fileInfos, errorLoading]);

    return (
        <div className={'preview-file__container'}>
            <Input
                uiClassName={`preview-file ${!errorLoading && fileInfos ? 'preview-file__on-click' : ''}`}
                onClick={onClick}
                readOnly
                value={fileName}
                rightIcon={onDelete && <Icon name="trash" onClick={onDelete} color={'black'} />}
                leftIcon={<Icon name="file" />}
            />
            {displayDate && fileInfos && <div className={'preview-file__last_modified'}>{t('nacre.previewFile.lastModified', { date: lastModifiedDate })}</div>}
            {errorLoading && <div className={'preview-file__error-loading'}>{t('nacre.previewFile.error.loading')}</div>}
        </div>
    );
}

export function UploadPreviewImage({
    isLoading = false,
    errorLoading = false,
    onLoad = () => {},
    onError = () => {},
    onDelete = () => {},
    fileSrc = null,
    fileTypes = [],
    onChangeEditImage = () => {}
}) {
    const { t } = useTranslation();

    const onClick = React.useCallback(() => {
        if (!errorLoading) {
            window.open(fileSrc);
        }
    }, [fileSrc, errorLoading]);

    return (
        <div className={'upload-preview-image'}>
            {isLoading && (
                <div className="upload-preview-image__loader-container">
                    <Loader className={'upload-preview-image__loader'} inline>
                        <span className={'upload-preview-image__loader-label'}>{t('ticket.common.loading')}</span>
                    </Loader>
                </div>
            )}
            <div className={!isLoading ? 'upload-preview-image__content-display' : 'upload-preview-image__content-hide'}>
                <div style={{ position: 'relative' }}>
                    {errorLoading && <PlaceholderErrorLoading />}
                    {!errorLoading && <img alt="event logo" className="upload-preview-image__content-image" src={fileSrc} onClick={onClick} onLoad={onLoad} onError={onError} />}
                    {!isLoading && <UploadPreviewPanelButton imageSrc={fileSrc} onChangeEditImage={onChangeEditImage} onDelete={onDelete} fileTypes={fileTypes} />}
                </div>
            </div>
        </div>
    );
}

export default function UploadPreview({ isImage = false, ...rest }) {
    return isImage ? <UploadPreviewImage {...rest} /> : <UploadPreviewFile {...rest} />;
}

UploadPreview.propTypes = {
    isImage: PropTypes.bool
};
